<template>
  <Teleport to="body">
    <transition name="floating-dropdown-fade">
      <div
        v-if="open && triggerRef"
        ref="menu"
        class="floating-dropdown-menu"
        :style="menuStyle"
        @mousedown="handleMousedown"
      >
        <slot />
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'FloatingDropdown',
  props: {
    open: {
      type: Boolean,
      required: true
    },
    triggerRef: {
      type: [HTMLElement, Object],
      default: null
    },
    matchWidth: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: 'left',
      validator: (v) => ['left', 'right'].includes(v)
    },
    offset: {
      type: Number,
      default: 4
    },
    // Allow text selection inside the dropdown. Off by default because
    // input-anchored dropdowns rely on mousedown preventDefault to keep
    // focus (and the dropdown itself) from being dismissed by a blur.
    selectable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['dismiss'],
  data() {
    return {
      position: { top: 0, left: 0, width: 0, maxHeight: null }
    }
  },
  computed: {
    menuStyle() {
      const style = {
        position: 'fixed',
        top: `${this.position.top}px`,
        left: `${this.position.left}px`,
        zIndex: 10000
      }
      if (this.matchWidth) {
        style.width = `${this.position.width}px`
      }
      if (this.position.maxHeight !== null) {
        style['--fd-max-height'] = `${this.position.maxHeight}px`
      }
      return style
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.activate()
      } else {
        this.deactivate()
      }
    },
    triggerRef(val) {
      if (val && this.open) {
        this.activate()
      }
    }
  },
  unmounted() {
    this.deactivate()
  },
  methods: {
    activate() {
      this.deactivate()
      this.$nextTick(() => {
        this.updatePosition()
        window.addEventListener('scroll', this.handleDismiss, true)
        window.addEventListener('resize', this.handleDismiss)
        requestAnimationFrame(() => {
          this.updatePosition()
        })
      })
    },
    deactivate() {
      window.removeEventListener('scroll', this.handleDismiss, true)
      window.removeEventListener('resize', this.handleDismiss)
    },
    updatePosition() {
      const trigger = this.triggerRef?.$el || this.triggerRef
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const menuEl = this.$refs.menu

      const bottomPadding = 10
      const spaceBelow = window.innerHeight - rect.bottom - this.offset - bottomPadding
      const spaceAbove = rect.top - this.offset - bottomPadding
      const menuHeight = menuEl ? menuEl.scrollHeight : 200

      let top
      let maxHeight
      if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
        top = rect.bottom + this.offset
        maxHeight = spaceBelow > 0 ? spaceBelow : null
      } else {
        const h = Math.min(menuHeight, spaceAbove)
        top = rect.top - this.offset - h
        maxHeight = spaceAbove > 0 ? spaceAbove : null
      }

      let left = rect.left
      if (this.align === 'right' && !this.matchWidth && menuEl) {
        left = rect.right - menuEl.offsetWidth
      }

      this.position = {
        top,
        left,
        width: rect.width,
        maxHeight
      }
    },
    handleMousedown(event) {
      if (this.selectable) return
      const tag = event.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      event.preventDefault()
    },
    handleDismiss(event) {
      // Don't dismiss if scrolling inside the dropdown itself
      if (event.type === 'scroll') {
        const menu = this.$refs.menu
        if (menu && menu.contains(event.target)) return
      }
      this.$emit('dismiss')
    },
    // Public method: check if an element is inside this dropdown
    containsElement(el) {
      const menu = this.$refs.menu
      return menu ? menu.contains(el) : false
    }
  }
}
</script>

<style>
.floating-dropdown-menu {
  border-radius: 4px;
}

/* Transition: fade in/out (0.15s) with slide */
.floating-dropdown-fade-enter-active,
.floating-dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.floating-dropdown-fade-enter-from,
.floating-dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
